/**
 * Texto e tema Mermaid para flowchart-widget (palco + export).
 */

export const DEFAULT_MERMAID_FLOWCHART = `flowchart TD
    A[Início] --> B{Tem acesso?}
    B -->|Sim| C[Conteúdo]
    B -->|Não| D[Solicitar]
    C --> E[Fim]
    D --> E`;

const MAX_LEN = 12000;

const ALLOWED_THEMES = ['default', 'neutral', 'dark', 'forest'];

/**
 * Paletas iniciais (somente hex) alinhadas aos temas nomeados do Mermaid 10.
 * Obrigatório usar theme: 'base' para que themeVariables (incl. nodeBkg / nodeBorder) surtam efeito.
 */
export const FLOWCHART_NAMED_THEME_VARIABLES = {
  default: {
    darkMode: false,
    background: '#ffffff',
    primaryColor: '#ececff',
    secondaryColor: '#ffffde',
    tertiaryColor: '#ececff',
    mainBkg: '#ececff',
    nodeBkg: '#ececff',
    nodeBorder: '#9370db',
    clusterBkg: '#ffffde',
    clusterBorder: '#aaaa33',
    lineColor: '#333333',
    arrowheadColor: '#333333',
    defaultLinkColor: '#333333',
    textColor: '#333333',
    primaryTextColor: '#333333',
    secondaryTextColor: '#333333',
    tertiaryTextColor: '#333333',
    nodeTextColor: '#333333',
    edgeLabelBackground: '#e8e8e8',
  },
  neutral: {
    darkMode: false,
    background: '#ffffff',
    primaryColor: '#eeeeee',
    secondaryColor: '#f4f4f4',
    tertiaryColor: '#eeeeee',
    mainBkg: '#eeeeee',
    nodeBkg: '#eeeeee',
    nodeBorder: '#999999',
    clusterBkg: '#f4f4f4',
    clusterBorder: '#707070',
    lineColor: '#666666',
    arrowheadColor: '#333333',
    defaultLinkColor: '#666666',
    textColor: '#333333',
    primaryTextColor: '#333333',
    secondaryTextColor: '#333333',
    tertiaryTextColor: '#333333',
    nodeTextColor: '#333333',
    edgeLabelBackground: '#ffffff',
  },
  dark: {
    darkMode: true,
    background: '#333333',
    primaryColor: '#1f2020',
    secondaryColor: '#2d2e2e',
    tertiaryColor: '#1f2020',
    mainBkg: '#1f2020',
    nodeBkg: '#1f2020',
    nodeBorder: '#81b1db',
    clusterBkg: '#2d2e2e',
    clusterBorder: '#555555',
    lineColor: '#cccccc',
    arrowheadColor: '#cccccc',
    defaultLinkColor: '#cccccc',
    textColor: '#cccccc',
    primaryTextColor: '#e0e0e0',
    secondaryTextColor: '#e0e0e0',
    tertiaryTextColor: '#e0e0e0',
    nodeTextColor: '#cccccc',
    edgeLabelBackground: '#3a3a3a',
  },
  forest: {
    darkMode: false,
    background: '#ffffff',
    primaryColor: '#cde498',
    secondaryColor: '#cdffb2',
    tertiaryColor: '#d4e8a8',
    mainBkg: '#cde498',
    nodeBkg: '#cde498',
    nodeBorder: '#13540c',
    clusterBkg: '#cdffb2',
    clusterBorder: '#6eaa49',
    lineColor: '#13540c',
    arrowheadColor: '#13540c',
    defaultLinkColor: '#13540c',
    textColor: '#333333',
    primaryTextColor: '#333333',
    secondaryTextColor: '#333333',
    tertiaryTextColor: '#333333',
    nodeTextColor: '#333333',
    edgeLabelBackground: '#e8e8e8',
  },
};

export function normalizeMermaidTheme(t) {
  const s = String(t || 'default').toLowerCase();
  return ALLOWED_THEMES.includes(s) ? s : 'default';
}

/** Normaliza texto Mermaid para comparar com o `source` de um preset. */
export function normalizeMermaidForPresetMatch(s) {
  return String(s ?? '').replace(/\r\n/g, '\n').trim();
}

/**
 * @param {{ id: string, source: string }[]} presets
 * @returns {string|null} id do preset se o texto for idêntico a um modelo; senão null.
 */
export function getMatchingFlowchartPresetId(presets, mermaidSource) {
  const n = normalizeMermaidForPresetMatch(mermaidSource);
  if (!n) return null;
  const list = Array.isArray(presets) ? presets : [];
  for (const p of list) {
    if (normalizeMermaidForPresetMatch(p?.source) === n) return p.id;
  }
  return null;
}

/** Limita tamanho e remove padrões perigosos para embedding em HTML/JSON. */
export function sanitizeMermaidSource(raw) {
  let s = String(raw ?? '').replace(/\r\n/g, '\n');
  if (s.length > MAX_LEN) s = s.slice(0, MAX_LEN);
  s = s.replace(/<\/(script|style)/gi, '');
  s = s.replace(/javascript:/gi, '');
  s = s.trim();
  if (!s) return DEFAULT_MERMAID_FLOWCHART;
  return s;
}

const FLOWCHART_FONT_MIN = 10;
const FLOWCHART_FONT_MAX = 40;
const FLOWCHART_FONT_DEFAULT = 16;

/** Tamanho da fonte do diagrama: só 10–40 px; fora disso usa o padrão (16). */
export function resolveFlowchartDiagramFontPx(raw) {
  const n = parseInt(raw, 10);
  if (Number.isFinite(n) && n >= FLOWCHART_FONT_MIN && n <= FLOWCHART_FONT_MAX) return n;
  return FLOWCHART_FONT_DEFAULT;
}

/** Primeira linha útil: flowchart LR ou graph LR (ignora %% e linhas vazias). */
export function isMermaidFlowchartLR(raw) {
  const s = String(raw ?? '').replace(/\r\n/g, '\n');
  for (const line of s.split('\n')) {
    const t = line.trim();
    if (!t || t.startsWith('%%')) continue;
    return /^(flowchart|graph)\s+LR\b/i.test(t);
  }
  return false;
}

export const FLOWCHART_NODE_PADDING_MIN = 8;
export const FLOWCHART_NODE_PADDING_MAX = 72;

/** Padding automático (rótulo ↔ borda do nó) quando o usuário não definiu o slider. */
export function getFlowchartNodePaddingDefault(isLr) {
  return isLr ? 72 : 20;
}

/**
 * Valor efetivo de `flowchart.padding` (Mermaid): controla o “tamanho” visual dos nós.
 * Fora do intervalo ou sem valor salvo → padrão por orientação (LR vs demais).
 */
export function resolveFlowchartNodePadding(content) {
  const c = content || {};
  const lr = isMermaidFlowchartLR(c.mermaidSource);
  const fallback = getFlowchartNodePaddingDefault(lr);
  const n = parseInt(c.flowchartNodePadding, 10);
  if (Number.isFinite(n) && n >= FLOWCHART_NODE_PADDING_MIN && n <= FLOWCHART_NODE_PADDING_MAX) return n;
  return fallback;
}

/** Espaço entre “fileiras” do grafo: em LR afeta o comprimento horizontal das setas; em TD, o espaço vertical entre níveis. */
export const FLOWCHART_RANK_SPACING_MIN = 24;
export const FLOWCHART_RANK_SPACING_MAX = 200;

export function getFlowchartRankSpacingDefault(isLr) {
  return isLr ? 140 : 80;
}

export function resolveFlowchartRankSpacing(content) {
  const c = content || {};
  const lr = isMermaidFlowchartLR(c.mermaidSource);
  const fallback = getFlowchartRankSpacingDefault(lr);
  const n = parseInt(c.flowchartRankSpacing, 10);
  if (Number.isFinite(n) && n >= FLOWCHART_RANK_SPACING_MIN && n <= FLOWCHART_RANK_SPACING_MAX) return n;
  return fallback;
}

/** Espaço entre nós no mesmo nível (ramos paralelos). */
export const FLOWCHART_NODE_SPACING_MIN = 16;
export const FLOWCHART_NODE_SPACING_MAX = 180;

export function getFlowchartNodeSpacingDefault(isLr) {
  return isLr ? 120 : 64;
}

export function resolveFlowchartNodeSpacing(content) {
  const c = content || {};
  const lr = isMermaidFlowchartLR(c.mermaidSource);
  const fallback = getFlowchartNodeSpacingDefault(lr);
  const n = parseInt(c.flowchartNodeSpacing, 10);
  if (Number.isFinite(n) && n >= FLOWCHART_NODE_SPACING_MIN && n <= FLOWCHART_NODE_SPACING_MAX) return n;
  return fallback;
}

/** Valida #hex (3 ou 6 dígitos); retorna null se inválido. */
export function sanitizeFlowchartHex(raw) {
  let s = String(raw ?? '').trim();
  if (!s || s === 'transparent') return null;
  let h = s.replace(/^#/, '');
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(h)) return null;
  if (h.length === 3) h = h.split('').map((ch) => ch + ch).join('');
  return `#${h.toLowerCase()}`;
}

/**
 * Opções para mermaid.initialize (palco + export).
 * theme: 'base' + paleta do tema (Geral) para nodeBkg/nodeBorder/fontSize surtirem efeito.
 * Cores extras: aba Colors; rótulos podem usar Cor do texto do bloco.
 */
export function buildFlowchartMermaidInitConfig(content) {
  const c = content || {};
  const fontPx = resolveFlowchartDiagramFontPx(c.flowDiagramFontPx);
  const lr = isMermaidFlowchartLR(c.mermaidSource);

  const themed = normalizeMermaidTheme(c.theme);
  const named = FLOWCHART_NAMED_THEME_VARIABLES[themed] || FLOWCHART_NAMED_THEME_VARIABLES.default;

  const nodeText = sanitizeFlowchartHex(c.flowNodeTextColor)
    || sanitizeFlowchartHex(c.textColor);
  const nodeBkg = sanitizeFlowchartHex(c.flowNodeBkg);
  const nodeBorder = sanitizeFlowchartHex(c.flowNodeBorder);
  const edge = sanitizeFlowchartHex(c.flowEdgeColor);
  const canvas = sanitizeFlowchartHex(c.flowCanvasBg);

  /** @type {Record<string, string | boolean>} */
  const themeVariables = { ...named, fontSize: `${fontPx}px` };

  if (nodeText) {
    themeVariables.primaryTextColor = nodeText;
    themeVariables.secondaryTextColor = nodeText;
    themeVariables.tertiaryTextColor = nodeText;
    themeVariables.textColor = nodeText;
    themeVariables.nodeTextColor = nodeText;
  }

  if (nodeBkg) {
    themeVariables.primaryColor = nodeBkg;
    themeVariables.secondaryColor = nodeBkg;
    themeVariables.tertiaryColor = nodeBkg;
    themeVariables.mainBkg = nodeBkg;
    themeVariables.nodeBkg = nodeBkg;
  }

  if (nodeBorder) {
    themeVariables.primaryBorderColor = nodeBorder;
    themeVariables.secondaryBorderColor = nodeBorder;
    themeVariables.tertiaryBorderColor = nodeBorder;
    themeVariables.nodeBorder = nodeBorder;
  }

  if (edge) {
    themeVariables.lineColor = edge;
    themeVariables.arrowheadColor = edge;
    themeVariables.defaultLinkColor = edge;
  }

  if (canvas) {
    themeVariables.background = canvas;
  }

  const nodePad = resolveFlowchartNodePadding(c);
  const rankSp = resolveFlowchartRankSpacing(c);
  const nodeSp = resolveFlowchartNodeSpacing(c);

  const flowchart = lr
    ? {
        /** LR largo: sem encolher à largura do bloco — nós legíveis + rolagem horizontal no host. */
        useMaxWidth: false,
        htmlLabels: true,
        nodeSpacing: nodeSp,
        rankSpacing: rankSp,
        padding: nodePad,
      }
    : {
        useMaxWidth: true,
        htmlLabels: true,
        nodeSpacing: nodeSp,
        rankSpacing: rankSp,
        padding: nodePad,
      };

  return {
    startOnLoad: false,
    theme: 'base',
    securityLevel: 'strict',
    fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    themeVariables,
    flowchart,
  };
}
